import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Save, X, BellRing, Loader2, Repeat, RotateCcw, Upload, ImageIcon, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, fmtTime12 } from "@/lib/format";

const empty = {
  title_es: "",
  title_en: "",
  description_es: "",
  description_en: "",
  event_date: "",
  event_time: "",
  location: "",
  image_url: "",
  flyer_url: "",
  registration_url: "",
  recurring_weekly: false,
  notify_day_of_week: 6, // Sunday default (Mon=0..Sun=6)
  notify_time: "08:00",
};

const MAX_BYTES = 8 * 1024 * 1024;

export default function AdminEvents() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);
  const [runningRecurring, setRunningRecurring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const flyerInputRef = useRef(null);

  const reload = async () => {
    try { const { data } = await api.get("/events"); setList(data); } catch (_) {}
  };
  useEffect(() => { reload(); }, []);

  const onSave = async () => {
    if (!editing.title_es || !editing.title_en || !editing.event_date) {
      toast.error(t.common.required);
      return;
    }
    // Normalize recurring fields when toggle is off so we don't ship stale UI state.
    const payload = { ...editing };
    if (!payload.recurring_weekly) {
      // Backend will accept null/empty; we still pass sensible values so re-toggle doesn't lose them.
      payload.notify_day_of_week = payload.notify_day_of_week ?? 6;
      payload.notify_time = payload.notify_time || "08:00";
    }
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(t.admin.saved);
      setOpen(false);
      reload();
    } catch (_) {
      toast.error(t.common.error);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try { await api.delete(`/admin/events/${id}`); toast.success(t.admin.deleted); reload(); } catch (_) { toast.error(t.common.error); }
  };

  const onNotify = async (ev) => {
    const titleStr = lang === "es" ? (ev.title_es || ev.title_en) : (ev.title_en || ev.title_es);
    if (!window.confirm(lang === "es"
      ? `Enviar notificacion push a todos los miembros sobre "${titleStr}"?`
      : `Send push notification to all members about "${titleStr}"?`)) return;
    setNotifyingId(ev.id);
    try {
      const { data } = await api.post(`/admin/events/${ev.id}/notify`);
      toast.success(lang === "es"
        ? `Notificacion enviada: ${data.sent} entregadas \u00b7 ${data.failed} fallidas`
        : `Notification sent: ${data.sent} delivered \u00b7 ${data.failed} failed`);
    } catch (_) {
      toast.error(t.common.error);
    } finally { setNotifyingId(null); }
  };

  const onRunRecurringNow = async () => {
    if (!window.confirm(lang === "es"
      ? "Enviar ahora los avisos de todos los eventos semanales? (Cada evento solo se envia una vez al dia.)"
      : "Send weekly reminders now for all recurring events? (Each event is sent at most once per day.)")) return;
    setRunningRecurring(true);
    try {
      const { data } = await api.post(`/admin/recurring/run`);
      const msg = (t.admin.eventsForm.runNowResult || "Fired {fired} events, {sent} sent, {failed} failed.")
        .replace("{fired}", data.fired || 0)
        .replace("{sent}", data.sent || 0)
        .replace("{failed}", data.failed || 0);
      toast.success(msg);
    } catch (_) {
      toast.error(t.common.error);
    } finally { setRunningRecurring(false); }
  };

  const onPickFlyer = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t.common.error);
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(lang === "es" ? "Archivo muy grande (max 8MB)" : "File too large (max 8MB)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("ncl_admin_token")) || "";
      const res = await fetch(`${API_BASE}/admin/events/upload-flyer`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      setEditing((cur) => ({ ...cur, flyer_url: data.flyer_url }));
      toast.success(t.admin.saved);
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
      if (flyerInputRef.current) flyerInputRef.current.value = "";
    }
  };

  const dayLabel = (idx) => (t.admin.eventsForm.dayNames?.[idx]) || `Day ${idx}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif-display text-2xl font-semibold">{t.admin.events}</h1>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onRunRecurringNow}
            disabled={runningRecurring}
            data-testid="admin-events-run-recurring"
          >
            {runningRecurring ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1 h-4 w-4" />}
            {runningRecurring ? t.admin.eventsForm.runNowRunning : t.admin.eventsForm.runNow}
          </Button>
          <Button onClick={() => { setEditing({ ...empty }); setOpen(true); }} data-testid="admin-events-add">
            <Plus className="mr-1 h-4 w-4" />{t.admin.addNew}
          </Button>
        </div>
      </div>

      {list.length === 0 ? (
        <Card className="rounded-2xl border-dashed bg-white/60">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-events-empty">{t.admin.noItems}</CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-events-list">
          {list.map((ev) => (
            <Card key={ev.id} className="rounded-2xl ring-1 ring-black/5">
              <CardContent className="flex items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-serif-display text-base font-semibold">{lang === "es" ? ev.title_es : ev.title_en}</p>
                    {ev.recurring_weekly && (
                      <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`admin-events-recurring-${ev.id}`}>
                        <Repeat className="h-3 w-3" />
                        {t.admin.eventsForm.recurringBadge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {fmtDate(ev.event_date, lang)}
                    {ev.event_time && ` \u00b7 ${fmtTime12(ev.event_time)}`}
                    {ev.location && ` \u00b7 ${ev.location}`}
                  </p>
                  {ev.recurring_weekly && (ev.notify_day_of_week !== undefined && ev.notify_day_of_week !== null) && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {(t.admin.eventsForm.everyDay || "Every {day}").replace("{day}", dayLabel(ev.notify_day_of_week))}
                      {ev.notify_time && ` \u00b7 ${fmtTime12(ev.notify_time)}`}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="outline" onClick={() => onNotify(ev)} disabled={notifyingId === ev.id} title={lang === "es" ? "Notificar a miembros" : "Notify members"} data-testid={`admin-events-notify-${ev.id}`}>
                    {notifyingId === ev.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(ev); setOpen(true); }} data-testid={`admin-events-edit-${ev.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(ev.id)} data-testid={`admin-events-delete-${ev.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="admin-events-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? t.common.edit : t.common.create} - {t.admin.events}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t.admin.eventsForm.title_es}>
                  <Input value={editing.title_es} onChange={(e) => setEditing({ ...editing, title_es: e.target.value })} data-testid="event-title-es" />
                </Field>
                <Field label={t.admin.eventsForm.title_en}>
                  <Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="event-title-en" />
                </Field>
              </div>
              <Field label={t.admin.eventsForm.description_es}>
                <Textarea rows={3} value={editing.description_es} onChange={(e) => setEditing({ ...editing, description_es: e.target.value })} data-testid="event-desc-es" />
              </Field>
              <Field label={t.admin.eventsForm.description_en}>
                <Textarea rows={3} value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} data-testid="event-desc-en" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label={t.admin.eventsForm.date}>
                  <Input type="date" value={editing.event_date} onChange={(e) => setEditing({ ...editing, event_date: e.target.value })} data-testid="event-date" />
                </Field>
                <Field label={t.admin.eventsForm.time}>
                  <Input type="time" value={editing.event_time} onChange={(e) => setEditing({ ...editing, event_time: e.target.value })} data-testid="event-time" />
                </Field>
                <Field label={t.admin.eventsForm.location}>
                  <Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="event-location" />
                </Field>
              </div>

              {/* Flyer upload */}
              <div className="space-y-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-medium">{t.admin.eventsForm.flyer}</Label>
                </div>
                <p className="text-xs text-muted-foreground">{t.admin.eventsForm.flyerHint}</p>
                {editing.flyer_url ? (
                  <div className="relative overflow-hidden rounded-xl ring-1 ring-black/5 bg-white">
                    <img
                      src={editing.flyer_url}
                      alt="event flyer preview"
                      className="block max-h-72 w-full object-contain"
                      data-testid="event-flyer-preview"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing({ ...editing, flyer_url: "" })}
                      className="absolute right-2 top-2 bg-white/90 backdrop-blur"
                      data-testid="event-flyer-remove"
                    >
                      <X className="mr-1 h-4 w-4" />
                      {t.admin.eventsForm.flyerRemove}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={flyerInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onPickFlyer}
                      className="hidden"
                      data-testid="event-flyer-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      onClick={() => flyerInputRef.current?.click()}
                      data-testid="event-flyer-upload"
                    >
                      {uploading
                        ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{t.admin.eventsForm.flyerUploading}</>
                        : <><Upload className="mr-1 h-4 w-4" />{t.admin.eventsForm.flyerUpload}</>}
                    </Button>
                  </div>
                )}
              </div>

              {/* Registration URL */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-medium">{t.admin.eventsForm.registrationUrl}</Label>
                </div>
                <Input
                  type="url"
                  inputMode="url"
                  placeholder="https://forms.google.com/..."
                  value={editing.registration_url || ""}
                  onChange={(e) => setEditing({ ...editing, registration_url: e.target.value })}
                  data-testid="event-registration-url"
                />
                <p className="text-[11px] text-muted-foreground">{t.admin.eventsForm.registrationUrlHint}</p>
              </div>

              {/* Weekly recurrence */}
              <div className="space-y-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="event-recurring" className="flex items-center gap-2 text-sm font-medium">
                      <Repeat className="h-4 w-4 text-primary" />
                      {t.admin.eventsForm.recurring}
                    </Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.admin.eventsForm.recurringHint}</p>
                  </div>
                  <Switch
                    id="event-recurring"
                    checked={!!editing.recurring_weekly}
                    onCheckedChange={(v) => setEditing({ ...editing, recurring_weekly: v })}
                    data-testid="event-recurring-toggle"
                  />
                </div>

                {editing.recurring_weekly && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t.admin.eventsForm.notifyDay}</Label>
                      <Select
                        value={String(editing.notify_day_of_week ?? 6)}
                        onValueChange={(v) => setEditing({ ...editing, notify_day_of_week: parseInt(v, 10) })}
                      >
                        <SelectTrigger data-testid="event-notify-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(t.admin.eventsForm.dayNames || []).map((dn, idx) => (
                            <SelectItem key={idx} value={String(idx)}>{dn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label={t.admin.eventsForm.notifyTime}>
                      <Input
                        type="time"
                        value={editing.notify_time || "08:00"}
                        onChange={(e) => setEditing({ ...editing, notify_time: e.target.value })}
                        data-testid="event-notify-time"
                      />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}><X className="mr-1 h-4 w-4" />{t.common.cancel}</Button>
            <Button onClick={onSave} data-testid="admin-events-save"><Save className="mr-1 h-4 w-4" />{t.common.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
    </div>
  );
}
